import { Popover, PopoverContent, PopoverTrigger, Icon as SpriteIcon } from '@glade-ai/ui';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { type FC, useCallback, useState } from 'react';

import DotsThreeCircle from '@/components/Icons/DotsThreeCircle';
import Tooltip from '@/components/Tooltip';
import { logError } from '@/providers/ErrorTracking';

import ICON_SIZES from './constants';
import s from './SecondaryActions.module.scss';

// Support both string icons (new sprite-based) and Phosphor components (legacy)
type IconProp = string | PhosphorIcon;

/**
 * Toggles sprite icon between regular and fill weights.
 * Assumes icon names follow format: ph--{name}--{weight}
 */
const toggleSpriteWeight = (icon: string, shouldFill: boolean): string => {
  if (shouldFill) {
    return icon.replace(/--regular$/, '--fill');
  }
  return icon.replace(/--fill$/, '--regular');
};

/**
 * Secondary actions dropdown menu component.
 *
 * Features:
 * - Supports sprite-based icons (string names like "ph--trash--regular")
 *   and Phosphor icon components for backwards compatibility
 * - Handles async operations - menu stays open with loading indicator
 * - Logs errors to Sentry and keeps menu open for retry
 * - All options disabled while one is pending
 *
 * @example
 * // Synchronous action
 * <SecondaryActions
 *   ariaLabel="Actions"
 *   options={[{ icon: 'ph--trash--regular', value: 'Delete' }]}
 *   onSelect={(value) => handleDelete(value)}
 * />
 *
 * @example
 * // Async action with loading state
 * <SecondaryActions
 *   ariaLabel="Actions"
 *   options={[{ icon: 'ph--trash--regular', value: 'Delete' }]}
 *   onSelect={async (value) => {
 *     await deleteItemFromAPI(itemId);
 *   }}
 * />
 */
type Props = {
  ariaLabel: string;
  options: Array<{
    value: string;
    icon?: IconProp;
    disabled?: boolean;
    destructive?: boolean;
  }>;
  onSelect: (value: string) => void | Promise<void>;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  customIcon?: IconProp;
  className?: string;
  compact?: boolean;
};

const SecondaryActions: FC<Props> = ({
  ariaLabel,
  options,
  onSelect,
  position = 'bottom-right',
  customIcon,
  className,
  compact,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingOption, setPendingOption] = useState<string | null>(null);

  const handleSelect = useCallback(
    async (value: string): Promise<void> => {
      setPendingOption(value);
      try {
        await onSelect(value);
        setIsOpen(false); // Only close after successful completion
      } catch (error) {
        logError(error, { tags: { flow: 'secondary-actions' } });
        // Keep popover open on error so user can retry
      } finally {
        setPendingOption(null);
      }
    },
    [onSelect],
  );

  const IconComponent = customIcon || DotsThreeCircle;
  const isStringIcon = typeof customIcon === 'string' && customIcon.length > 0;

  // Convert position to Radix Popover side and align
  const [side, align] = (() => {
    switch (position) {
      case 'top-left':
        return ['top', 'start'] as const;
      case 'top-right':
        return ['top', 'end'] as const;
      case 'bottom-left':
        return ['bottom', 'start'] as const;
      case 'bottom-right':
      default:
        return ['bottom', 'end'] as const;
    }
  })();

  if (options.length === 0) return null;

  return (
    <div className={classNames(s.container, compact && s.containerCompact, className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip text={ariaLabel} alignment={position.includes('left') ? 'bottom-left' : 'bottom-right'}>
          <PopoverTrigger asChild>
            <button type='button' aria-label={ariaLabel} className={s.trigger}>
              {isStringIcon ? (
                <SpriteIcon
                  icon={toggleSpriteWeight(customIcon, isOpen)}
                  size={compact ? ICON_SIZES.TRIGGER.COMPACT_SPRITE : ICON_SIZES.TRIGGER.NORMAL_SPRITE}
                />
              ) : (
                <IconComponent
                  weight={isOpen ? 'fill' : 'regular'}
                  size={compact ? ICON_SIZES.TRIGGER.COMPACT_PHOSPHOR : ICON_SIZES.TRIGGER.NORMAL_PHOSPHOR}
                />
              )}
            </button>
          </PopoverTrigger>
        </Tooltip>
        <PopoverContent
          side={side}
          align={align}
          sideOffset={4}
          className={classNames('p-0 w-auto', s.menu, compact && s.menuCompact)}
        >
          <ul>
            {options.map((option) => (
              <li key={option.value}>
                <button
                  className={s.menuItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(option.value);
                  }}
                  disabled={option.disabled || pendingOption !== null}
                  style={{
                    color: option.destructive ? 'var(--color-error)' : 'var(--color-gray-100)',
                  }}
                >
                  {option.icon &&
                    (() => {
                      if (pendingOption === option.value) {
                        return (
                          <SpriteIcon
                            icon='ph--circle-notch--regular'
                            size={ICON_SIZES.LOADING.SPRITE}
                            className='animate-spin'
                          />
                        );
                      }
                      if (typeof option.icon === 'string') {
                        return <SpriteIcon icon={option.icon} size={ICON_SIZES.OPTION.SPRITE} />;
                      }
                      return <option.icon size={ICON_SIZES.OPTION.PHOSPHOR} weight='fill' />;
                    })()}
                  {option.value}
                </button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SecondaryActions;
